import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../../../../../context/AuthContext';
import OrgTreeSelect from '../../../../../components/OrgSelect/OrgTreeSelect';
import { EditorContent } from '@tiptap/react';
import EditorToolbar from '../../../../../components/editor/EditorToolbar';
import tiptapExtensions from '../../../../../components/editor/tiptapExtensions';
import useEditorWithDragDrop from '../../../../../components/editor/useEditorWithDragDrop';
import FileAttachment from '../../../../../components/FileAttachment/FileAttachment';
import CommonDatePicker from '../../../../../components/DatePicker/CommonDatePicker';

function SafetyTrainingModal({ mode, initialData, onClose, onSave, onDelete, onStartEdit, canEdit }) {
  const { user } = useContext(AuthContext);

  const [formDate, setFormDate] = useState('');
  const [formTime, setFormTime] = useState('');
  const [formDuration, setFormDuration] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formInstructor, setFormInstructor] = useState('');
  const [formType, setFormType] = useState('');
  const [formAttendeeCnt, setFormAttendeeCnt] = useState('');
  const [formCompleteCnt, setFormCompleteCnt] = useState('');
  const [formContent, setFormContent] = useState('');
  const [files, setFiles] = useState([]);
  const [editorHeight, setEditorHeight] = useState('400px');

  // 대상자 관련 (회의록과 동일한 구조)
  const [assignees, setAssignees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  const [orgPopupOpen, setOrgPopupOpen] = useState(false);         // 강사용
  const [attendeeOrgPopupOpen, setAttendeeOrgPopupOpen] = useState(false); // 대상자용
  const [isAttendeeExpanded, setIsAttendeeExpanded] = useState(false);
  const [showExpandBtn, setShowExpandBtn] = useState(false);
  const attendeeListRef = useRef(null);

  useEffect(() => {
    if (attendeeListRef.current) {
      setShowExpandBtn(attendeeListRef.current.scrollHeight > 130);
    }
  }, [assignees, mode, isAttendeeExpanded, formAttendeeCnt]);

  // Editor hook
  const editor = useEditorWithDragDrop(tiptapExtensions);

  useEffect(() => {
    if (initialData) {
      setFormDate(initialData.date || '');
      setFormTime(initialData.time || '');
      setFormDuration(initialData.duration || '');
      setFormLocation(initialData.location || '');
      setFormInstructor(initialData.instructor || '');
      setFormType(initialData.type || initialData.title || '');
      // 대상자 복원 (배열이면 assignees 형식으로, 아니면 빈 배열)
      const att = Array.isArray(initialData.attendees) ? initialData.attendees : [];
      const converted = att.map((a, i) => ({
        emp_no: a.user_id ? String(a.user_id) : `manual_${i}`,
        name: a.user_name || '',
        position: a.user_position || '',
        department: a.dept_name || '',
        checked: false
      }));
      setAssignees(converted);
      setFormAttendeeCnt(converted.length > 0 ? String(converted.length) : (initialData.attendeeCnt || '0'));
      setFormCompleteCnt(initialData.completeCnt || '0');
      setFormContent(initialData.content || '');
      setFiles(initialData.files || []);
      setEditorHeight('400px');

      if (initialData.date && initialData.time) {
        const dateParts = initialData.date.split(' ~ ').map(s => s.trim());
        const startDateStr = dateParts[0];
        const endDateStr = dateParts.length > 1 ? dateParts[1] : dateParts[0];

        const timeParts = initialData.time.split('~').map(s => s.trim());
        if (timeParts.length === 2) {
          const sd = new Date(`${startDateStr}T${timeParts[0]}:00`);
          const ed = new Date(`${endDateStr}T${timeParts[1]}:00`);
          if (!isNaN(sd.getTime())) setStartDateTime(sd);
          if (!isNaN(ed.getTime())) setEndDateTime(ed);
        }
      }

      if (editor && mode === 'WRITE') {
        editor.commands.setContent(initialData.content || '');
      }
    }
  }, [initialData, mode, editor]);

  const updateDateAndDuration = (start, end) => {
    if (!start && !end) return;

    let s = start || end;
    let e = end || start;

    if (e.getTime() < s.getTime()) {
      alert('종료 일시가 시작 일시보다 빠를 수 없습니다.');
      setEndDateTime(s);
      e = s;
    }

    const pad = (n) => String(n).padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const formatTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

    const startStr = formatDate(s);
    const endStr = formatDate(e);
    setFormDate(startStr === endStr ? startStr : `${startStr} ~ ${endStr}`);
    setFormTime(`${formatTime(s)} ~ ${formatTime(e)}`);

    // 오직 시/분 단위 차이만 계산 (날짜 무시)
    const startHour = s.getHours() + s.getMinutes() / 60;
    const endHour = e.getHours() + e.getMinutes() / 60;
    let diffHours = endHour - startHour;

    // 밤을 넘기는 시간대일 경우 (예: 23:00 ~ 01:00) 24시간을 더해 보정
    if (diffHours < 0) diffHours += 24;

    if (diffHours > 0) {
      const formatted = diffHours.toFixed(1);
      setFormDuration(formatted.endsWith('.0') ? formatted.slice(0, -2) : formatted);
    } else {
      setFormDuration('0');
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    const contentText = editor ? editor.getText().trim() : (formContent || '').replace(/<[^>]*>?/gm, '').trim();

    if (!formType.trim()) return alert('교육종류(제목)를 입력해 주세요.');
    if (!formDate.trim()) return alert('날짜(교육 일시)를 입력해 주세요.');
    if (!formLocation.trim()) return alert('교육장소를 입력해 주세요.');
    if (!formInstructor.trim()) return alert('교육강사를 입력해 주세요.');
    if (!formAttendeeCnt || Number(formAttendeeCnt) <= 0 || assignees.length === 0) return alert('교육 대상자를 1명 이상 등록해 주세요.');
    if (!contentText) return alert('교육내용을 입력해 주세요.');

    const docData = {
      title: formType,
      date: formDate,
      time: formTime,
      duration: formDuration,
      location: formLocation,
      instructor: formInstructor,
      type: formType,
      attendeeCnt: formAttendeeCnt,
      completeCnt: formCompleteCnt,
      content: editor ? editor.getHTML() : formContent,
      files: files,
      attendees: assignees.map(a => ({
        user_id: String(a.emp_no).startsWith('manual_') ? null : String(a.emp_no),
        user_name: a.name || '',
        dept_name: a.department || '',
        user_position: a.position || ''
      }))
    };

    onSave(docData);
  };

  // 강사 조직도 확인
  const handleOrgConfirm = (selectedTargets) => {
    setOrgPopupOpen(false);
    if (!selectedTargets || selectedTargets.length === 0) return;
    const instructorName = selectedTargets.map(t => `${t.target_name} ${t.position || ''}`).join(', ');
    setFormInstructor(instructorName);
  };

  // 대상자 추가 (회의록과 동일)
  const addAssignee = (u) => {
    setAssignees(prev => {
      if (prev.some(c => String(c.emp_no) === String(u.userid))) return prev;
      const newList = [...prev, {
        emp_no: u.userid,
        name: u.name,
        position: u.position || '',
        department: u.dept_name || u.department || '',
        checked: false
      }];
      setFormAttendeeCnt(String(newList.length));
      return newList;
    });
  };

  const deleteAll = () => { setAssignees([]); setFormAttendeeCnt('0'); };

  const handleAttendeeSearch = (term) => {
    const keyword = (term || '').trim();
    if (!keyword) { alert('검색대상이 입력되지 않았습니다.'); return; }
    const token = user?.token || sessionStorage.getItem('token');
    axios.get('/api/org/search', { params: { keyword }, headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const users = res.data.users || [];
        if (users.length === 0) { alert('검색 결과가 존재하지 않습니다.'); return; }
        users.forEach(u => addAssignee(u));
        setSearchTerm('');
      }).catch(err => console.error('검색 실패:', err));
  };


  // 대상자 조직도 확인
  const handleAttendeeOrgConfirm = (selectedTargets) => {
    setAttendeeOrgPopupOpen(false);
    if (!selectedTargets || selectedTargets.length === 0) return;
    setAssignees(prev => {
      const manuals = prev.filter(a => String(a.emp_no).startsWith('manual_'));
      const formatted = selectedTargets.map(t => ({
        emp_no: t.target_id || t.user_id || t.id,
        name: t.target_name || t.name || '',
        position: t.position || '',
        department: t.dept_name || '',
        checked: false
      }));
      const merged = [...manuals, ...formatted];
      setFormAttendeeCnt(String(merged.length));
      return merged;
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
    } catch {
      return dateStr;
    }
  };

  const isView = mode === 'DETAIL';

  return ReactDOM.createPortal(
    <div className="modal show d-block" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9998, overflow: 'hidden' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1000px', width: '95%' }}>
        <div className="modal-content" style={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>

          <div className="modal-header d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', color: '#fff', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', padding: '16px 24px' }}>
            <h5 className="modal-title fw-bold m-0 fs-5">
              <i className={`fas ${isView ? 'fa-eye' : 'fa-edit'} me-2`}></i>
              {isView ? '위험성 평가 교육일지 상세 정보' : (initialData ? '교육일지 수정' : '새 교육일지 작성')}
            </h5>
            <div className="d-flex align-items-center gap-3">
              {isView && initialData && (
                <div className="d-flex align-items-center gap-3" style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', paddingRight: '12px', borderRight: '1px solid rgba(255,255,255,0.3)', marginTop: '4px' }}>
                  <span><i className="fas fa-user-edit me-1"></i>{initialData.author || '-'}</span>
                  <span><i className="fas fa-clock me-1"></i>{formatDateTime(initialData.createdAt)}</span>
                </div>
              )}
              <button type="button" className="btn-close btn-close-white" onClick={onClose} style={{ boxShadow: 'none' }}></button>
            </div>
          </div>

          <div className="modal-body" style={{ padding: '20px 30px', height: '65vh', minHeight: '450px', overflowY: 'auto', backgroundColor: '#f8fafc' }}>
            <div className="row g-3">
              {/* 교육종류 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-graduation-cap me-1 text-primary"></i> 교육종류 (제목) {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{formType || '-'}</div>
                  ) : (
                    <input type="text" className="form-control" value={formType} onChange={e => setFormType(e.target.value)} placeholder="예: 위험성평가 실시 결과 및 유해위험요인 교육" style={{ fontSize: '0.825rem' }} />
                  )}
                </div>
              </div>

              {/* 교육 일시 및 시간 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-calendar-alt me-1 text-primary"></i> 교육 일시 및 시간 {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="d-flex flex-wrap gap-3">
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>
                        <span className="text-secondary me-2">날짜:</span>{formDate || '-'}
                      </div>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>
                        <span className="text-secondary me-2">시간:</span>{formTime || '-'}
                      </div>
                      <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>
                        <span className="text-secondary me-2">소요시간:</span>{formDuration || 0}시간
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-3 flex-wrap date-picker-pointer">
                      <style>{`
                          .date-picker-pointer input {
                            cursor: pointer !important;
                          }
                          .modal-body .form-control::placeholder {
                            color: #adb5bd !important;
                            opacity: 0.7 !important;
                            font-weight: 400 !important;
                          }
                        `}</style>

                      {/* 날짜 직접 입력란 + 시작일/종료일 달력 */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-semibold text-secondary" style={{ fontSize: '0.825rem' }}>날짜:</span>
                        <input type="text" className="form-control" value={formDate} onChange={e => setFormDate(e.target.value)} placeholder="예: 2026-08-11 또는 2026-08-11 ~ 2026-08-12" style={{ width: '310px', height: '34px', fontSize: '0.825rem' }} />

                        <div style={{ position: 'relative', zIndex: 1050, marginLeft: '4px' }}>
                          <CommonDatePicker
                            value={startDateTime}
                            onChange={date => {
                              setStartDateTime(date);
                              if (!endDateTime) setEndDateTime(date);
                              updateDateAndDuration(date, endDateTime || date);
                            }}
                            showTime={true}
                            useButton={true}
                            buttonLabel={<i className="fas fa-calendar-alt" title="시작 일시 선택"></i>}
                          />
                        </div>
                        <span className="text-secondary fw-bold">~</span>
                        <div style={{ position: 'relative', zIndex: 1050 }}>
                          <CommonDatePicker
                            value={endDateTime}
                            onChange={date => {
                              setEndDateTime(date);
                              updateDateAndDuration(startDateTime, date);
                            }}
                            showTime={true}
                            useButton={true}
                            buttonLabel={<i className="fas fa-calendar-check" title="종료 일시 선택"></i>}
                            minDate={startDateTime}
                            minTime={startDateTime && endDateTime && startDateTime.toDateString() === endDateTime.toDateString() ? startDateTime : undefined}
                          />
                        </div>
                      </div>

                      {/* 시간 직접 입력란 */}
                      <div className="d-flex align-items-center gap-2 border-start ps-3" style={{ borderColor: '#e2e8f0' }}>
                        <span className="fw-semibold text-secondary" style={{ fontSize: '0.825rem' }}>시간:</span>
                        <input type="text" className="form-control" value={formTime} onChange={e => setFormTime(e.target.value)} placeholder="예: 14:30~15:30" style={{ width: '130px', height: '34px', fontSize: '0.825rem' }} />
                      </div>

                      {/* 소요시간 직접 입력란 */}
                      <div className="d-flex align-items-center gap-2 border-start ps-3" style={{ borderColor: '#e2e8f0' }}>
                        <span className="fw-semibold text-secondary" style={{ fontSize: '0.825rem' }}>소요시간:</span>
                        <input type="text" className="form-control text-center" value={formDuration} onChange={e => setFormDuration(e.target.value)} placeholder="예: 1" style={{ width: '60px', height: '34px', fontSize: '0.825rem' }} />
                        <span style={{ fontSize: '0.825rem' }}>시간</span>
                      </div>

                    </div>
                  )}
                </div>
              </div>

              {/* 교육장소 */}
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-map-marker-alt me-1 text-primary"></i> 교육장소 {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{formLocation || '-'}</div>
                  ) : (
                    <input type="text" className="form-control" value={formLocation} onChange={e => setFormLocation(e.target.value)} placeholder="예: 직원 휴게실" style={{ fontSize: '0.825rem' }} />
                  )}
                </div>
              </div>

              {/* 교육강사 */}
              <div className="col-md-6">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-user-tie me-1 text-primary"></i> 교육강사 {!isView && <span className="text-danger">*</span>}
                  </label>
                  {isView ? (
                    <div className="text-dark fw-bold px-3 py-2 rounded" style={{ fontSize: '0.825rem', backgroundColor: '#f1f5f9' }}>{formInstructor || '-'}</div>
                  ) : (
                    <div className="d-flex gap-2">
                      <input type="text" className="form-control flex-grow-1" value={formInstructor} onChange={e => setFormInstructor(e.target.value)} placeholder="조직도 검색 또는 직접 입력" style={{ fontSize: '0.825rem' }} />
                      <button type="button" className="btn btn-sm text-nowrap flex-shrink-0" style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '0 8px', borderRadius: '4px', height: '34px' }} onClick={() => setOrgPopupOpen(true)}>
                        <i className="fas fa-sitemap me-1"></i>조직도
                      </button>
                      <button type="button" className="btn btn-sm text-nowrap flex-shrink-0" style={{ backgroundColor: '#fff', border: '1px solid #dc3545', color: '#dc3545', padding: '0 8px', borderRadius: '4px', height: '34px' }} onClick={() => setFormInstructor('')}>
                        <i className="fas fa-trash-alt me-1"></i>삭제
                      </button>
                    </div>
                  )}
                </div>
              </div>


              {/* 교육 대상자 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-users me-1 text-primary"></i> 교육 대상자 {!isView && <span className="text-danger">*</span>}
                      {assignees.length > 0 && <span className="ms-2 fw-semibold" style={{ fontSize: '0.75rem', color: '#64748b' }}>(총 {assignees.length}명)</span>}
                    </label>
                    {showExpandBtn && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '0px 6px', fontSize: '12px' }}
                        onClick={() => setIsAttendeeExpanded(!isAttendeeExpanded)}
                        title={isAttendeeExpanded ? "접기" : "펼쳐보기"}
                      >
                        <i className={`fas ${isAttendeeExpanded ? 'fa-minus' : 'fa-plus'}`}></i>
                      </button>
                    )}
                  </div>
                  <div className="card shadow-sm border-0 bg-light bg-opacity-50 mb-0">
                    <div className="card-body p-2">
                      {isView ? (
                        <div ref={attendeeListRef} className="d-flex flex-wrap gap-2 align-items-center bg-white p-2 border rounded" style={{ maxHeight: isAttendeeExpanded ? 'none' : '130px', overflowY: 'auto', transition: 'max-height 0.3s ease' }}>
                          {assignees.length > 0 ? assignees.map((a, i) => (
                            <span key={i} className="badge" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                              {a.name}{a.position ? ` ${a.position}` : ''}{a.department ? ` (${a.department})` : ''}
                            </span>
                          )) : <span className="text-muted" style={{ fontSize: '13px' }}>-</span>}
                        </div>
                      ) : (
                        <>
                          <div className="form-schedule-search-bar mb-2 d-flex align-items-center w-100">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (searchTerm.trim()) handleAttendeeSearch(searchTerm); } }}
                                placeholder="이름 검색"
                                className="form-control d-inline-block w-auto"
                                style={{ minWidth: '200px', fontSize: '13px', height: '32px' }}
                              />
                              <button type="button" className="btn btn-sm" style={{ backgroundColor: '#343a40', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => handleAttendeeSearch(searchTerm)}>검색</button>
                              <button type="button" className="btn btn-sm" style={{ backgroundColor: 'transparent', border: '1px solid #007bff', color: '#007bff', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={() => setAttendeeOrgPopupOpen(true)}>조직도 선택</button>
                            </div>
                            {assignees.length > 0 && (
                              <button type="button" className="btn btn-sm text-nowrap ms-auto" style={{ backgroundColor: '#fff', border: '1px solid #dc3545', color: '#dc3545', padding: '4px 12px', borderRadius: '4px', whiteSpace: 'nowrap' }} onClick={deleteAll}>
                                <i className="fas fa-trash-alt me-1"></i>전체삭제
                              </button>
                            )}
                          </div>
                          {assignees.length > 0 ? (
                            <div ref={attendeeListRef} className="d-flex flex-wrap gap-2 border rounded p-3 bg-white" style={{ maxHeight: isAttendeeExpanded ? 'none' : '130px', overflowY: 'auto', transition: 'max-height 0.3s ease' }}>
                              {assignees.map((a, i) => (
                                <span key={a.emp_no} className="badge d-flex align-items-center gap-2" style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600, fontSize: '13px', padding: '5px 10px' }}>
                                  <span>{a.name}{a.position ? ` ${a.position}` : ''}{a.department ? ` (${a.department})` : ''}</span>
                                  <i className="fas fa-times-circle text-primary" style={{ cursor: 'pointer' }} onClick={() => {
                                    setAssignees(prev => {
                                      const res = prev.filter(item => item.emp_no !== a.emp_no);
                                      setFormAttendeeCnt(String(res.length));
                                      return res;
                                    });
                                  }}></i>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 border rounded bg-white text-muted" style={{ fontSize: '0.8rem' }}>
                              지정된 교육 대상자가 없습니다. 이름 검색 또는 조직도 버튼을 눌러 추가해 주세요.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 교육인원 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border" style={{ borderColor: '#e2e8f0' }}>
                  <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                    <i className="fas fa-users me-1 text-primary"></i> 교육인원 {!isView && <span className="text-danger">*</span>}
                  </label>
                  <div className="d-flex align-items-center flex-wrap" style={{ fontSize: '0.85rem' }}>
                    {/* 교육대상 */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold text-secondary">교육대상 :</span>
                      {isView ? (
                        <span className="fw-bold text-dark">{formAttendeeCnt || '0'} 명</span>
                      ) : (
                        <div className="d-flex align-items-center gap-1">
                          <input type="number" className="form-control text-center" value={formAttendeeCnt} readOnly style={{ width: '80px', height: '34px', fontSize: '0.825rem', margin: 0, backgroundColor: '#f1f5f9', cursor: 'not-allowed' }} />
                          <span>명</span>
                        </div>
                      )}
                    </div>

                    <span className="text-muted fw-bold mx-3">/</span>

                    {/* 수료 */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold text-secondary">수료 :</span>
                      {isView ? (
                        <span className="fw-bold text-success">{formCompleteCnt || '0'} 명</span>
                      ) : (
                        <div className="d-flex align-items-center gap-1">
                          <input type="number" className="form-control text-center" value={formCompleteCnt} onChange={e => {
                            const val = Math.min(Number(e.target.value), Number(formAttendeeCnt || 0));
                            setFormCompleteCnt(String(val < 0 ? 0 : val));
                          }} placeholder="예: 1" max={formAttendeeCnt} min={0} style={{ width: '80px', height: '34px', fontSize: '0.825rem', margin: 0 }} />
                          <span>명</span>
                        </div>
                      )}
                    </div>

                    <span className="text-muted fw-bold mx-3">|</span>

                    {/* 미수료 */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold text-secondary">미수료 :</span>
                      <span className="fw-bold text-danger">
                        {Math.max(0, parseInt(formAttendeeCnt || 0) - parseInt(formCompleteCnt || 0))} 명
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 교육내용 */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0', height: editorHeight, transition: 'height 0.3s ease' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-bold text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-file-alt me-1 text-primary"></i> 교육내용 {!isView && <span className="text-danger">*</span>}
                    </label>
                    {!isView && (
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-secondary fw-semibold" style={{ fontSize: '0.8rem' }}>에디터 높이:</span>
                        <select
                          className="form-select form-select-sm text-secondary"
                          style={{ width: '130px', fontSize: '0.8rem', cursor: 'pointer' }}
                          value={editorHeight}
                          onChange={(e) => setEditorHeight(e.target.value)}
                        >
                          <option value="400px">기본 (400px)</option>
                          <option value="600px">넓게 (600px)</option>
                          <option value="800px">아주 넓게 (800px)</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1 bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                    {isView ? (
                      <div className="post-content bg-white p-3 rounded shadow-sm h-100 overflow-auto">
                        <style>{`
                          .post-content p { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
                          .post-content { font-size: 10pt; line-height: 1.2; }
                        `}</style>
                        <div dangerouslySetInnerHTML={{ __html: formContent }} />
                      </div>
                    ) : (
                      <div className="border p-1 rounded bg-white h-100 d-flex flex-column">
                        {editor && <EditorToolbar editor={editor} />}
                        <div className="flex-grow-1 overflow-auto bg-white" style={{ minHeight: 0 }}>
                          <style>{`
                            .ProseMirror p { margin-top: 0; margin-bottom: 0.2em; font-size: 10pt; }
                            .ProseMirror { font-size: 10pt; line-height: 1.2; }
                          `}</style>
                          <EditorContent editor={editor} className="editor-content h-100" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 비고 (첨부파일) */}
              {(!isView || (files && files.filter(f => !f.isDeleted).length > 0)) && (
                <div className="col-12">
                  <div className="p-3 bg-white rounded-3 shadow-sm border d-flex flex-column" style={{ borderColor: '#e2e8f0' }}>
                    <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                      <i className="fas fa-paperclip me-1 text-primary"></i> 비고 (첨부파일)
                    </label>
                    <div className="flex-grow-1 overflow-auto bg-light rounded-2 p-1" style={{ minHeight: 0 }}>
                      <FileAttachment files={files} setFiles={setFiles} mode={isView ? 'view' : 'edit'} />
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="modal-footer" style={{ borderTop: '1px solid #cbd5e1', backgroundColor: '#f1f5f9', padding: '12px 24px', display: 'flex', justifyContent: 'flex-end' }}>
            <div className="d-flex" style={{ gap: '8px' }}>
              {isView ? (
                <>
                  {canEdit && <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onStartEdit}>수정</button>}
                  {canEdit && <button type="button" className="btn btn-danger btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onDelete}>삭제</button>}
                  <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>닫기</button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-primary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={handleSave}>저장</button>
                  <button type="button" className="btn btn-secondary btn-sm px-3" style={{ fontWeight: 'bold', fontSize: '0.8rem' }} onClick={onClose}>취소</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 강사 조직도 팝업 */}
      {orgPopupOpen && (
        <OrgTreeSelect
          selectionMode="multi"
          targetType="user"
          initialSelected={[]}
          onConfirm={handleOrgConfirm}
          onCancel={() => setOrgPopupOpen(false)}
        />
      )}

      {/* 대상자 조직도 팝업 */}
      {attendeeOrgPopupOpen && (
        <OrgTreeSelect
          selectionMode="multi"
          targetType="user"
          initialSelected={assignees.map(a => ({
            target_type: 'user',
            target_id: String(a.emp_no),
            target_name: a.name,
            position: a.position || '',
            dept_name: a.department || '',
            disp_target_name: `${a.name}${a.position ? " / " + a.position : ""}${a.department ? " (" + a.department + ")" : ""}`
          }))}
          onConfirm={handleAttendeeOrgConfirm}
          onCancel={() => setAttendeeOrgPopupOpen(false)}
        />
      )}
    </div>,
    document.body
  );
}

export default SafetyTrainingModal;
